import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRightIcon,
  CharacterCard,
  Eyebrow,
  Fleuron,
  PhoneIcon,
  StepRow,
  Swoosh,
  TestimonialCard,
  TrustTile,
  Wordmark,
} from "../components/design";
import {
  VITANARA_PHONE_DISPLAY,
  VITANARA_PHONE_TEL,
  VITANARA_BERATUNG_DISPLAY,
  VITANARA_BERATUNG_TEL,
} from "../lib/contact";
import { useAuth } from "../lib/auth";
import PublicFooter from "../components/PublicFooter";
import Hoerproben from "../components/Hoerproben";

// Mobile-first canonical width 390px. Centred 520px column on desktop with
// the paper-bg filling the viewport.

export default function Landing() {
  return (
    <div
      className="paper-bg"
      style={{
        minHeight: "100vh",
        fontFamily: "var(--f-body)",
        color: "var(--ink)",
      }}
    >
      <div className="desk-container-landing">
        <TopBar />
        <Hero />
        <HowItWorks />
        <Biographers />
        <Excerpt />
        <Hoerproben />
        <Testimonials />
        <Trust />
        <ClosingCTA />
      </div>
      <PublicFooter />
      <StickyCTA />
    </div>
  );
}

// Floating CTA — appears once the hero is scrolled past, links to onboarding.
function StickyCTA() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!visible) return null;
  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: "calc(14px + env(safe-area-inset-bottom))",
        display: "flex",
        justifyContent: "center",
        zIndex: 50,
        pointerEvents: "none",
      }}
    >
      <Link
        to="/onboarding"
        className="btn-primary"
        style={{
          pointerEvents: "auto",
          padding: "13px 24px",
          minHeight: 0,
          fontSize: 15,
          boxShadow:
            "0 2px 0 var(--oxblood-d), 0 12px 28px rgba(43,26,15,0.35)",
        }}
      >
        Kostenlos starten
        <ArrowRightIcon />
      </Link>
    </div>
  );
}

function TopBar() {
  const { user } = useAuth();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 14,
        padding: "60px 22px 0",
      }}
    >
      <Wordmark />
      <nav
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
          justifyContent: "flex-end",
        }}
      >
        <a
          href={`tel:${VITANARA_BERATUNG_TEL}`}
          title="Kundenberatung"
          style={{
            display: "none",
            alignItems: "center",
            gap: 6,
            fontFamily: "var(--f-ui)",
            fontSize: 13,
            color: "var(--ink-soft)",
            textDecoration: "none",
          }}
          data-mq-sm
        >
          <PhoneIcon />
          Beratung {VITANARA_BERATUNG_DISPLAY}
        </a>
        {user ? (
          <Link
            to="/dashboard"
            className="btn-secondary"
            style={{
              padding: "9px 16px",
              minHeight: 0,
              fontSize: 14,
            }}
          >
            Mein Bereich
          </Link>
        ) : (
          <Link
            to="/login"
            style={{
              fontFamily: "var(--f-ui)",
              fontSize: 14,
              color: "var(--ink-soft)",
              textDecoration: "none",
              padding: "8px 4px",
            }}
          >
            Anmelden
          </Link>
        )}
      </nav>
      <style>{`
        @media (min-width: 640px) {
          [data-mq-sm] { display: inline-flex !important; }
        }
      `}</style>
    </div>
  );
}

function Hero() {
  return (
    <section style={{ padding: "36px 22px 12px", position: "relative" }}>
      <div className="desk-hero-grid">
        <div className="hero-side">
          <Eyebrow>Am Telefon erzählt · Kapitel für Kapitel zum Buch</Eyebrow>
          <h1
            style={{
              fontFamily: "var(--f-display)",
              fontWeight: 400,
              fontSize: 44,
              lineHeight: 1.02,
              letterSpacing: "-0.02em",
              margin: "0 0 14px",
              color: "var(--ink)",
              textWrap: "pretty",
            }}
          >
            Damit ihre{" "}
            <Swoosh>
              <em>Geschichte</em>
            </Swoosh>
            <br />
            bleibt.
          </h1>
          <div
            className="character-pair"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 18,
              margin: "4px 0 20px",
              maxWidth: 460,
            }}
          >
            <CharacterCard who="konrad" name="Konrad" desc="hört gerne zu" />
            <CharacterCard who="ida" name="Ida" desc="fragt nach Details" />
          </div>
          <p
            style={{
              fontFamily: "var(--f-body)",
              fontSize: 18,
              lineHeight: 1.45,
              color: "var(--ink-soft)",
              margin: "0 0 18px",
              maxWidth: 460,
            }}
          >
            Einfach anrufen und erzählen — Tag und Nacht. Konrad und Ida
            hören zu und fragen nach, wie zwei Biografen am Telefon. Aus
            jedem Gespräch wächst ein PDF, Kapitel für Kapitel — bis Ihr
            Buch fertig ist.
          </p>
          <Link
            to="/onboarding"
            className="btn-primary"
            style={{ width: "100%", boxSizing: "border-box", maxWidth: 420 }}
          >
            Kostenlos starten
            <ArrowRightIcon />
          </Link>
          <div
            style={{
              marginTop: 10,
              fontFamily: "var(--f-ui)",
              fontSize: 12,
              color: "var(--ink-faint)",
            }}
          >
            <strong style={{ color: "var(--ink-soft)" }}>Friends &amp; Family Beta</strong>{" "}
            · kostenlos · Festnetz genügt · DSGVO-konform
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      n: "01",
      t: "Anrufen, wann immer",
      d: "Konrad und Ida sind Tag und Nacht erreichbar. Einfach den Hörer nehmen und loslegen — kein Termin, kein Smartphone nötig.",
    },
    {
      n: "02",
      t: "Erzählen & interviewt werden",
      d: "Sie hören zu und fragen nach, wie zwei Biografen. Aus jedem Gespräch wird Text — in den eigenen Worten.",
    },
    {
      n: "03",
      t: "Kapitel für Kapitel zum Buch",
      d: "Ein PDF wächst mit jedem Anruf. Sie lesen live mit, wie sich das Buch füllt — bis es fertig ist.",
    },
  ];
  return (
    <section
      className="paper-bg-dark"
      style={{
        padding: "36px 22px 40px",
        marginTop: 20,
        borderTop: "1px solid rgba(43,26,15,0.08)",
        borderBottom: "1px solid rgba(43,26,15,0.08)",
      }}
    >
      <Eyebrow>So funktioniert's</Eyebrow>
      <h2
        style={{
          fontFamily: "var(--f-display)",
          fontWeight: 400,
          fontStyle: "italic",
          fontSize: 32,
          lineHeight: 1.05,
          letterSpacing: "-0.015em",
          margin: "0 0 24px",
          color: "var(--ink)",
        }}
      >
        Anrufen, erzählen —
        <br />
        das Buch wächst mit.
      </h2>
      <div className="desk-steps-grid">
        {steps.map((s) => (
          <StepRow key={s.n} n={s.n} title={s.t} desc={s.d} />
        ))}
      </div>

      <div
        style={{
          marginTop: 18,
          fontFamily: "var(--f-body)",
          fontSize: 14,
          fontStyle: "italic",
          color: "var(--ink-faint)",
        }}
      >
        <Link
          to="/wie-funktioniert-vitanara"
          style={{ color: "var(--oxblood)", textDecoration: "none" }}
        >
          Mehr lesen: Wie funktioniert Vitanara? →
        </Link>
      </div>

      {/* Full-bleed accent image — Konrad on the phone */}
      <div
        style={{
          position: "relative",
          marginTop: 32,
          borderRadius: 6,
          overflow: "hidden",
          border: "1px solid rgba(43,26,15,0.10)",
          boxShadow:
            "0 1px 0 rgba(43,26,15,0.04), 0 14px 24px -8px rgba(43,26,15,0.18)",
        }}
      >
        {/* Tape strip top-left */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 10,
            left: 14,
            width: 60,
            height: 16,
            background: "rgba(168,122,58,0.55)",
            boxShadow: "0 1px 2px rgba(43,26,15,0.10)",
            transform: "rotate(-2deg)",
            zIndex: 2,
          }}
        />
        <img
          src="/images/konrad-phone.jpg"
          alt="Konrad am Telefon"
          className="photo-tone"
          style={{ display: "block", width: "100%", height: "auto" }}
        />
      </div>
    </section>
  );
}

function Biographers() {
  const experts = [
    {
      img: "/images/konrad.jpg",
      name: "Konrad Lebensfaden",
      role: "Spezialist für Lebensmeilensteine",
      text: "Konrad führt strukturiert durch die wichtigsten Lebensstationen. Er erkennt die Wendepunkte und verknüpft sie zu einer zusammenhängenden Erzählung.",
      quote: "„Jede Erfahrung hat ihren Platz im großen Lebensbuch.“",
    },
    {
      img: "/images/ida.jpg",
      name: "Ida Wurzelwerk",
      role: "Expertin für Familiengeschichten",
      text: "Ida hat ein Gespür für die leisen Töne. Sie erfasst die Emotionen zwischen den Zeilen und bringt die menschlichen Verbindungen zum Leuchten.",
      quote: "„In jeder Familie stecken Generationen von Weisheit.“",
    },
  ];
  return (
    <section style={{ padding: "40px 22px 8px" }}>
      <Eyebrow>Ihre Biografen</Eyebrow>
      <h2
        style={{
          fontFamily: "var(--f-display)",
          fontWeight: 400,
          fontSize: 28,
          lineHeight: 1.1,
          margin: "0 0 20px",
          color: "var(--ink)",
        }}
      >
        Zwei, die <em>zuhören</em> können.
      </h2>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: 14,
        }}
      >
        {experts.map((e) => (
          <div
            key={e.name}
            style={{
              background: "var(--paper-50)",
              border: "1px solid rgba(43,26,15,0.10)",
              borderRadius: 14,
              padding: "18px 18px 16px",
              boxShadow: "0 4px 12px rgba(43,26,15,0.06)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img
                src={e.img}
                alt={e.name}
                className="photo-tone"
                loading="lazy"
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "2px solid var(--paper-200)",
                  flexShrink: 0,
                }}
              />
              <div>
                <div
                  style={{
                    fontFamily: "var(--f-display)",
                    fontWeight: 500,
                    fontSize: 17,
                    color: "var(--ink)",
                  }}
                >
                  {e.name}
                </div>
                <div
                  style={{
                    fontFamily: "var(--f-ui)",
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--ochre)",
                  }}
                >
                  {e.role}
                </div>
              </div>
            </div>
            <p
              style={{
                fontFamily: "var(--f-body)",
                fontSize: 14.5,
                lineHeight: 1.5,
                color: "var(--ink-soft)",
                margin: "12px 0 10px",
              }}
            >
              {e.text}
            </p>
            <div
              style={{
                fontFamily: "var(--f-script)",
                fontSize: 18,
                color: "var(--oxblood)",
                transform: "rotate(-0.8deg)",
              }}
            >
              {e.quote}
            </div>
          </div>
        ))}
      </div>

      {/* Ida am Telefon — accent image */}
      <div
        style={{
          position: "relative",
          marginTop: 18,
          borderRadius: 6,
          overflow: "hidden",
          border: "1px solid rgba(43,26,15,0.10)",
          boxShadow:
            "0 1px 0 rgba(43,26,15,0.04), 0 14px 24px -8px rgba(43,26,15,0.18)",
        }}
      >
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 10,
            right: 14,
            width: 60,
            height: 16,
            background: "rgba(168,122,58,0.55)",
            boxShadow: "0 1px 2px rgba(43,26,15,0.10)",
            transform: "rotate(2deg)",
            zIndex: 2,
          }}
        />
        <img
          src="/images/ida-phone.jpg"
          alt="Ida Wurzelwerk am Telefon"
          className="photo-tone"
          loading="lazy"
          style={{ display: "block", width: "100%", height: "auto" }}
        />
      </div>
      <div
        style={{
          marginTop: 12,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          background: "var(--paper-50)",
          border: "1px solid rgba(43,26,15,0.10)",
          borderRadius: 999,
          padding: "8px 16px",
          fontFamily: "var(--f-ui)",
          fontSize: 13,
          color: "var(--ink-soft)",
        }}
      >
        <PhoneIcon />
        <span>
          <strong style={{ color: "var(--ink)" }}>Schon registriert?</strong>{" "}
          Mit der registrierten Nummer anrufen:{" "}
          <a
            href={`tel:${VITANARA_PHONE_TEL}`}
            style={{ color: "var(--oxblood)", textDecoration: "none", fontWeight: 600 }}
          >
            {VITANARA_PHONE_DISPLAY}
          </a>{" "}
          — Tag und Nacht.
        </span>
      </div>
    </section>
  );
}

function Excerpt() {
  return (
    <section style={{ padding: "40px 22px 34px", position: "relative" }}>
      <div className="desk-excerpt-wrap">
      <Eyebrow>Ihr Buch, live</Eyebrow>
      <h2
        style={{
          fontFamily: "var(--f-display)",
          fontWeight: 400,
          fontSize: 28,
          lineHeight: 1.1,
          margin: "0 0 20px",
          color: "var(--ink)",
        }}
      >
        Ein Kapitel, frisch aus dem <em>Gespräch</em>
      </h2>

      <div
        style={{
          background: "var(--paper-50)",
          borderRadius: 4,
          padding: "28px 22px 24px",
          border: "1px solid rgba(43,26,15,0.10)",
          boxShadow:
            "0 1px 0 rgba(43,26,15,0.04), 0 14px 24px -8px rgba(43,26,15,0.14)",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -10,
            right: 14,
            fontFamily: "var(--f-script)",
            fontSize: 18,
            color: "var(--ochre)",
            transform: "rotate(-3deg)",
          }}
        >
          Kapitel 4
        </div>
        <p
          className="dropcap"
          style={{
            fontFamily: "var(--f-display)",
            fontSize: 18,
            lineHeight: 1.55,
            color: "var(--ink)",
            margin: 0,
            textWrap: "pretty",
          }}
        >
          Den ersten Tanz mit Helmut werde ich nie vergessen. Es war im August
          1957, im Saal der „Linde" in Marbach. Ich trug ein Sommerkleid in
          Apfelgrün, das meine Mutter aus Vorhangstoff genäht hatte — und
          obwohl es nicht regnete, hatte er einen Schirm dabei.{" "}
          <em>Für alle Fälle</em>, sagte er, und lachte.
        </p>
        <Fleuron style={{ margin: "20px 0 4px" }} />
        <div
          style={{
            fontFamily: "var(--f-script)",
            fontSize: 22,
            color: "var(--oxblood)",
            textAlign: "right",
            transform: "rotate(-1deg)",
          }}
        >
          — Hildegard, 87
        </div>
      </div>

      <div
        style={{
          marginTop: 18,
          display: "flex",
          gap: 10,
          alignItems: "center",
          fontFamily: "var(--f-ui)",
          fontSize: 13,
          color: "var(--ink-faint)",
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6" />
        </svg>
        Neues Kapitel nach jedem Anruf — Sie lesen sofort im PDF mit.
        <Link
          to="/wie-funktioniert-vitanara"
          style={{
            marginLeft: 6,
            color: "var(--oxblood)",
            textDecoration: "none",
            fontStyle: "italic",
          }}
        >
          Mehr lesen →
        </Link>
      </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const cards = [
    {
      text: "Ich höre meinen Opa noch heute reden, wenn ich das Buch aufschlage. Das gibt es sonst nirgends.",
      who: "Marie, 41",
      rel: "Enkelin",
      rot: -1.2,
    },
    {
      text: "Oma greift inzwischen fast jeden Tag zum Hörer. Plötzlich erzählt sie Geschichten, die niemand kannte.",
      who: "Jonas, 38",
      rel: "Enkel",
      rot: 0.8,
    },
    {
      text: "Das Buch lag zu Weihnachten unter dem Baum. Wir haben drei Stunden lang vorgelesen — und geweint.",
      who: "Familie Kreuzer",
      rel: "aus Würzburg",
      rot: -0.5,
      photo: "/images/family-reading.jpg",
      photoAlt: "Familie Kreuzer beim Vorlesen",
    },
  ];
  return (
    <section
      className="paper-bg-dark"
      style={{
        padding: "38px 0 38px",
        borderTop: "1px solid rgba(43,26,15,0.08)",
        borderBottom: "1px solid rgba(43,26,15,0.08)",
      }}
    >
      <div style={{ padding: "0 22px", marginBottom: 20 }}>
        <Eyebrow>Was Familien sagen</Eyebrow>
        <h2
          style={{
            fontFamily: "var(--f-display)",
            fontWeight: 400,
            fontStyle: "italic",
            fontSize: 30,
            lineHeight: 1.05,
            margin: 0,
            color: "var(--ink)",
          }}
        >
          Briefe, die uns
          <br />
          erreicht haben.
        </h2>
      </div>
      <div className="desk-testimonials">
        {cards.map((c, i) => (
          <TestimonialCard key={i} {...c} />
        ))}
      </div>
    </section>
  );
}

function Trust() {
  const items: { i: "lock" | "shield" | "phone" | "pause"; t: string; d: string }[] = [
    {
      i: "lock",
      t: "DSGVO-konform",
      d: "Server in Frankfurt. Verschlüsselung auf jedem Schritt.",
    },
    {
      i: "shield",
      t: "Alles bleibt bei ihr",
      d: "Aufnahmen, Texte und Rechte gehören ausschließlich der erzählenden Person. Wir geben nichts weiter, verkaufen nichts.",
    },
    {
      i: "phone",
      t: "Echte Telefonnummer",
      d: "Kein Smartphone nötig. Ein Festnetzanschluss genügt.",
    },
    {
      i: "pause",
      t: "Jederzeit Pause",
      d: "Krankheit, Reise, schwere Tage — Pausieren mit einem Anruf.",
    },
  ];
  return (
    <section style={{ padding: "36px 22px 30px" }}>
      <Eyebrow>Vertrauen</Eyebrow>
      <h2
        style={{
          fontFamily: "var(--f-display)",
          fontWeight: 400,
          fontSize: 28,
          lineHeight: 1.1,
          margin: "0 0 22px",
          color: "var(--ink)",
        }}
      >
        Mit der Sorgfalt, die <em>Erinnerungen</em> verdienen.
      </h2>
      <div className="desk-trust-grid">
        {items.map((it) => (
          <TrustTile key={it.t} icon={it.i} title={it.t} desc={it.d} />
        ))}
      </div>
    </section>
  );
}

function ClosingCTA() {
  return (
    <section
      className="paper-deep"
      style={{ padding: "40px 22px 40px", position: "relative" }}
    >
      <div className="desk-closing-inner">
      <div
        className="t-caps"
        style={{ marginBottom: 14, color: "rgba(244,236,216,0.5)" }}
      >
        Jede Geschichte beginnt mit einem Anruf
      </div>
      <h2
        style={{
          fontFamily: "var(--f-display)",
          fontWeight: 400,
          fontSize: 34,
          lineHeight: 1.05,
          letterSpacing: "-0.015em",
          margin: "0 0 16px",
          color: "var(--paper-50)",
        }}
      >
        Nehmen Sie den Hörer.<br />
        Der Rest{" "}
        <span style={{ color: "var(--ochre)", fontStyle: "italic" }}>
          schreibt sich
        </span>
        .
      </h2>
      <p
        style={{
          fontFamily: "var(--f-body)",
          fontSize: 16,
          lineHeight: 1.5,
          color: "rgba(244,236,216,0.75)",
          margin: "0 0 8px",
        }}
      >
        In fünf Minuten startklar — danach kann jederzeit losgerufen und
        erzählt werden. Das PDF wächst mit jedem Gespräch.
      </p>
      <p
        style={{
          fontFamily: "var(--f-body)",
          fontStyle: "italic",
          fontSize: 13,
          lineHeight: 1.5,
          color: "rgba(244,236,216,0.55)",
          margin: "0 0 26px",
        }}
      >
        Alle Inhalte und Rechte bleiben bei der erzählenden Person.
      </p>
      <Link
        to="/onboarding"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          width: "100%",
          boxSizing: "border-box",
          background: "var(--paper-50)",
          color: "var(--oxblood-d)",
          fontFamily: "var(--f-ui)",
          fontWeight: 600,
          fontSize: 16,
          padding: "16px 22px",
          borderRadius: 999,
          textDecoration: "none",
          boxShadow:
            "0 2px 0 var(--paper-300), 0 10px 24px rgba(0,0,0,0.20)",
          minHeight: 50,
        }}
      >
        Kostenlos starten
        <ArrowRightIcon />
      </Link>
      <div
        style={{
          marginTop: 16,
          fontFamily: "var(--f-script)",
          fontSize: 22,
          color: "var(--ochre)",
          textAlign: "center",
        }}
      >
        Fragen vorab? Kundenberatung:{" "}
        <a
          href={`tel:${VITANARA_BERATUNG_TEL}`}
          style={{ color: "var(--ochre)", textDecoration: "underline", textUnderlineOffset: 3 }}
        >
          {VITANARA_BERATUNG_DISPLAY}
        </a>
      </div>
      <div
        style={{
          marginTop: 8,
          fontFamily: "var(--f-body)",
          fontStyle: "italic",
          fontSize: 13,
          color: "rgba(244,236,216,0.65)",
          textAlign: "center",
        }}
      >
        Schon registriert? Dann mit der registrierten Nummer hier anrufen:{" "}
        <a
          href={`tel:${VITANARA_PHONE_TEL}`}
          style={{ color: "rgba(244,236,216,0.9)", textDecoration: "underline", textUnderlineOffset: 3 }}
        >
          {VITANARA_PHONE_DISPLAY}
        </a>
      </div>
      </div>
    </section>
  );
}

